import React from 'react';

/**
 * CardImage Component
 * Displays a professional-looking credit card representation based on issuer/network
 */
function CardImage({ card, size = 'medium' }) {
  const cardName = card?.cardName?.toLowerCase() || '';
  const bank = card?.bank?.toLowerCase() || '';
  const network = card?.network?.toLowerCase() || '';

  // Determine card style based on card details
  const getCardStyle = () => {
    // American Express cards
    if (bank.includes('amex') || bank.includes('american express') || network.includes('american express')) {
      if (cardName.includes('platinum')) {
        return 'amex-platinum';
      }
      if (cardName.includes('gold')) {
        return 'amex-gold';
      }
      if (cardName.includes('green')) {
        return 'amex-green';
      }
      if (cardName.includes('blue')) {
        return 'amex-blue';
      }
      return 'amex-default';
    }

    // Chase cards
    if (bank.includes('chase') || cardName.includes('sapphire') || cardName.includes('freedom')) {
      if (cardName.includes('reserve')) {
        return 'chase-sapphire-reserve';
      }
      if (cardName.includes('preferred')) {
        return 'chase-sapphire-preferred';
      }
      if (cardName.includes('freedom')) {
        return 'chase-freedom';
      }
      return 'chase-default';
    }

    // Capital One cards
    if (bank.includes('capital one') || cardName.includes('venture') || cardName.includes('savor') || cardName.includes('quicksilver')) {
      if (cardName.includes('venture x')) {
        return 'capital-one-venture-x';
      }
      if (cardName.includes('venture')) {
        return 'capital-one-venture';
      }
      if (cardName.includes('savor')) {
        return 'capital-one-savor';
      }
      if (cardName.includes('quicksilver')) {
        return 'capital-one-quicksilver';
      }
      return 'capital-one-default';
    }

    // Citi cards
    if (bank.includes('citi') || cardName.includes('double cash') || cardName.includes('premier')) {
      if (cardName.includes('double cash')) {
        return 'citi-double-cash';
      }
      if (cardName.includes('premier')) {
        return 'citi-premier';
      }
      if (cardName.includes('custom cash')) {
        return 'citi-custom-cash';
      }
      return 'citi-default';
    }

    // Network-based fallbacks
    if (network.includes('visa')) {
      return 'visa-default';
    }
    if (network.includes('mastercard')) {
      return 'mastercard-default';
    }
    if (network.includes('discover')) {
      return 'discover-default';
    }

    return 'generic';
  };

  const getNetworkLogo = () => {
    if (network.includes('american express') || bank.includes('amex') || bank.includes('american express')) {
      return 'AMEX';
    }
    if (network.includes('visa')) {
      return 'VISA';
    }
    if (network.includes('mastercard')) {
      return 'MC';
    }
    if (network.includes('discover')) {
      return 'DISCOVER';
    }
    return '';
  };

  const getIssuerName = () => {
    if (bank.includes('amex') || bank.includes('american express')) {
      return 'American Express';
    }
    if (bank.includes('chase')) {
      return 'CHASE';
    }
    if (bank.includes('capital one')) {
      return 'CAPITAL ONE';
    }
    if (bank.includes('citi')) {
      return 'Citi';
    }
    return bank.toUpperCase();
  };

  const getDisplayName = () => {
    if (card?.cardName) {
      // Clean up card name for display
      let name = card.cardName;
      // Remove issuer name if it's repeated
      name = name.replace(/american express/gi, '').replace(/amex/gi, '').trim();
      name = name.replace(/chase/gi, '').trim();
      name = name.replace(/capital one/gi, '').trim();
      name = name.replace(/citi/gi, '').trim();
      return name || card.cardName;
    }
    return '';
  };

  const cardStyle = getCardStyle();
  const networkLogo = getNetworkLogo();
  const issuerName = getIssuerName();
  const displayName = getDisplayName();

  return (
    <div className={`card-image card-image-${size} card-style-${cardStyle}`}>
      <div className="card-image-inner">
        {/* Card chip */}
        <div className="card-chip">
          <div className="chip-line"></div>
          <div className="chip-line"></div>
          <div className="chip-line"></div>
          <div className="chip-line"></div>
        </div>

        {/* Issuer name */}
        <div className="card-issuer">{issuerName}</div>

        {/* Card name */}
        <div className="card-display-name">{displayName}</div>

        {/* Network logo */}
        <div className="card-network-logo">{networkLogo}</div>

        {/* Decorative elements */}
        <div className="card-decoration"></div>
      </div>
    </div>
  );
}

export default CardImage;
